import { z } from "zod";

/**
 * The Jobs page's filters, sort, and columns, and the saved views that keep them. A view's state is
 * also what the page's address holds (?stageIds=...&sort=...), so a link opens the same list.
 */

export const JOB_SORTS = ["updated", "newest", "oldest", "number", "customer", "stage", "stageAge", "contract"] as const;
export type JobSort = (typeof JOB_SORTS)[number];
export const JOB_SORT_LABELS: Record<JobSort, string> = {
  updated: "Last updated",
  newest: "Newest",
  oldest: "Oldest",
  number: "Job #",
  customer: "Customer name",
  stage: "Stage",
  stageAge: "Longest in stage",
  contract: "Contract amount",
};

export const JOB_COLUMNS = ["address", "stage", "rep", "updated", "created", "leadSource", "stageDays", "contract", "balance", "claim", "carrier"] as const;
export type JobColumn = (typeof JOB_COLUMNS)[number];
export const JOB_COLUMN_LABELS: Record<JobColumn, string> = {
  address: "Address",
  stage: "Stage",
  rep: "Rep",
  updated: "Updated",
  created: "Created",
  leadSource: "Lead source",
  stageDays: "Days in stage",
  contract: "Contract",
  balance: "Balance due",
  claim: "Claim #",
  carrier: "Carrier",
};
/** Columns that show money (only for people who can see it). */
export const MONEY_COLUMNS: readonly JobColumn[] = ["contract", "balance"];
export const DEFAULT_JOB_COLUMNS: JobColumn[] = ["address", "stage", "rep", "updated"];

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const jobFiltersSchema = z.object({
  q: z.string().trim().max(100).default(""),
  workflowId: z.string().uuid().nullable().default(null),
  stageIds: z.array(z.string().uuid()).max(100).default([]),
  /** Only each job type's first stage (new leads). */
  firstStage: z.boolean().default(false),
  /** A user, "none" (nobody), or "me" (whoever is looking). */
  repId: z.union([z.string().uuid(), z.literal("none"), z.literal("me")]).nullable().default(null),
  status: z.enum(["open", "closed", "lost", "all"]).default("open"),
  leadSources: z.array(z.string().max(80)).max(50).default([]),
  /** City or ZIP. */
  place: z.string().trim().max(80).default(""),
  /** In the same stage at least this many days. */
  stageDaysMin: z.number().int().min(1).max(3650).nullable().default(null),
  createdFrom: day.nullable().default(null),
  createdTo: day.nullable().default(null),
  updatedFrom: day.nullable().default(null),
  updatedTo: day.nullable().default(null),
});
export type JobFilters = z.infer<typeof jobFiltersSchema>;
export const DEFAULT_JOB_FILTERS: JobFilters = jobFiltersSchema.parse({});

export const jobViewStateSchema = z.object({
  filters: jobFiltersSchema.default(DEFAULT_JOB_FILTERS),
  sort: z.enum(JOB_SORTS).default("updated"),
  columns: z.array(z.enum(JOB_COLUMNS)).min(1).max(JOB_COLUMNS.length).default(DEFAULT_JOB_COLUMNS),
});
export type JobViewState = z.infer<typeof jobViewStateSchema>;
export const DEFAULT_JOB_VIEW: JobViewState = jobViewStateSchema.parse({});

export const jobViewInputSchema = jobViewStateSchema.extend({
  name: z.string().trim().min(1).max(60),
  /** Shared with these roles (none ticked: everyone); false: just mine. */
  shared: z.boolean().default(false),
  roleIds: z.array(z.string().uuid()).max(20).default([]),
});
export type JobViewInput = z.infer<typeof jobViewInputSchema>;
/** Changes: only what's sent (no defaults, so a partial change never resets the rest). */
export const jobViewUpdateSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  shared: z.boolean().optional(),
  roleIds: z.array(z.string().uuid()).max(20).optional(),
  filters: jobFiltersSchema.optional(),
  sort: z.enum(JOB_SORTS).optional(),
  columns: z.array(z.enum(JOB_COLUMNS)).min(1).max(JOB_COLUMNS.length).optional(),
});

export interface JobViewItem extends JobViewState {
  id: string;
  name: string;
  shared: boolean;
  roleIds: string[];
  /** Made by them (or they can manage shared views): they can change or delete it. */
  canEdit: boolean;
  ownerName: string | null;
}

export interface JobViewsResponse {
  views: JobViewItem[];
  /** The view that opens for them; null: Default view. */
  defaultViewId: string | null;
}

/** The view state as the page's address (only what differs from the defaults). */
export function jobViewToParams(v: JobViewState): URLSearchParams {
  const p = new URLSearchParams();
  const f = v.filters;
  if (f.q) p.set("q", f.q);
  if (f.workflowId) p.set("workflowId", f.workflowId);
  if (f.stageIds.length) p.set("stageIds", f.stageIds.join(","));
  if (f.firstStage) p.set("firstStage", "1");
  if (f.repId) p.set("repId", f.repId);
  if (f.status !== "open") p.set("status", f.status);
  if (f.leadSources.length) p.set("leadSources", f.leadSources.join("|"));
  if (f.place) p.set("place", f.place);
  if (f.stageDaysMin) p.set("stageDaysMin", String(f.stageDaysMin));
  for (const k of ["createdFrom", "createdTo", "updatedFrom", "updatedTo"] as const) if (f[k]) p.set(k, f[k]!);
  if (v.sort !== "updated") p.set("sort", v.sort);
  if (v.columns.join(",") !== DEFAULT_JOB_COLUMNS.join(",")) p.set("columns", v.columns.join(","));
  return p;
}

/** The view state from the page's address (anything unreadable falls back to the default). */
export function jobViewFromParams(p: URLSearchParams): JobViewState {
  const list = (k: string, sep = ",") => (p.get(k) ?? "").split(sep).filter(Boolean);
  const num = (k: string) => (p.get(k) ? Number(p.get(k)) : null);
  // Older links: ?stageId=<one>.
  const stageIds = list("stageIds").length ? list("stageIds") : list("stageId");
  const raw = {
    filters: {
      q: p.get("q") ?? "",
      workflowId: p.get("workflowId") || null,
      stageIds,
      firstStage: p.get("firstStage") === "1",
      repId: p.get("repId") || null,
      status: p.get("status") || "open",
      leadSources: list("leadSources", "|"),
      place: p.get("place") ?? "",
      stageDaysMin: num("stageDaysMin"),
      createdFrom: p.get("createdFrom") || null,
      createdTo: p.get("createdTo") || null,
      updatedFrom: p.get("updatedFrom") || null,
      updatedTo: p.get("updatedTo") || null,
    },
    sort: p.get("sort") || "updated",
    columns: list("columns").length ? list("columns") : DEFAULT_JOB_COLUMNS,
  };
  const r = jobViewStateSchema.safeParse(raw);
  if (r.success) return r.data;
  // Keep what's readable, field by field.
  const f = jobFiltersSchema.safeParse(raw.filters);
  return { filters: f.success ? f.data : DEFAULT_JOB_FILTERS, sort: (JOB_SORTS as readonly string[]).includes(raw.sort) ? (raw.sort as JobSort) : "updated", columns: raw.columns.filter((c): c is JobColumn => (JOB_COLUMNS as readonly string[]).includes(c)).length ? (raw.columns.filter((c) => (JOB_COLUMNS as readonly string[]).includes(c)) as JobColumn[]) : DEFAULT_JOB_COLUMNS };
}

/** Two view states are the same list (for "this view has changes"). */
export const sameJobView = (a: JobViewState, b: JobViewState) => jobViewToParams(a).toString() === jobViewToParams(b).toString();
